import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { LangProvider } from "@/context/LangContext";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";

import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import MemberRoute from "@/components/MemberRoute";

import Home from "@/pages/Home";
import CategoryPage from "@/pages/CategoryPage";
import ListingDetail from "@/pages/ListingDetail";
import SearchResults from "@/pages/SearchResults";
import ConnectPage from "@/pages/ConnectPage";
import { InsightsPage, InsightDetail, AboutPage } from "@/pages/ContentPages";
import OpportunitiesList from "@/pages/modules/OpportunitiesList";
import OpportunityDetail from "@/pages/modules/OpportunityDetail";
import ProjectsList from "@/pages/modules/ProjectsList";
import ProjectDetail from "@/pages/modules/ProjectDetail";
import InvestmentsList from "@/pages/modules/InvestmentsList";
import InvestmentDetail from "@/pages/modules/InvestmentDetail";
import PartnersList from "@/pages/modules/PartnersList";
import PartnerDetail from "@/pages/modules/PartnerDetail";
import ExpertsList from "@/pages/modules/ExpertsList";
import ExpertDetail from "@/pages/modules/ExpertDetail";
import SourcingList from "@/pages/modules/SourcingList";
import SourcingDetail from "@/pages/modules/SourcingDetail";
import MarketsList from "@/pages/modules/MarketsList";
import MarketDetail from "@/pages/modules/MarketDetail";
import MarketTestPage from "@/pages/modules/MarketTestPage";

import SignupPage from "@/pages/member/SignupPage";
import MemberLogin from "@/pages/member/MemberLogin";
import VerifyEmail from "@/pages/member/VerifyEmail";
import Account from "@/pages/member/Account";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ListingsAdmin from "@/pages/admin/ListingsAdmin";
import ListingEditor from "@/pages/admin/ListingEditor";
import SuggestionsAdmin from "@/pages/admin/SuggestionsAdmin";
import RequestsAdmin from "@/pages/admin/RequestsAdmin";
import ExpertsAdmin from "@/pages/admin/ExpertsAdmin";
import InsightsAdmin from "@/pages/admin/InsightsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import AccessAdmin from "@/pages/admin/AccessAdmin";
import MediaManager from "@/pages/admin/MediaManager";
import TaxonomiesAdmin from "@/pages/admin/TaxonomiesAdmin";
import SubmissionsAdmin from "@/pages/admin/SubmissionsAdmin";
import MatchingAdmin from "@/pages/admin/MatchingAdmin";
import OpportunitiesAdmin from "@/pages/admin/OpportunitiesAdmin";
import ProjectsAdmin from "@/pages/admin/ProjectsAdmin";
import InvestmentsAdmin from "@/pages/admin/InvestmentsAdmin";
import PartnersAdmin from "@/pages/admin/PartnersAdmin";
import ExpertProfilesAdmin from "@/pages/admin/ExpertProfilesAdmin";
import SourcingAdmin from "@/pages/admin/SourcingAdmin";
import MarketsAdmin from "@/pages/admin/MarketsAdmin";
import MarketTestsAdmin from "@/pages/admin/MarketTestsAdmin";
import { usePageTracking } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  usePageTracking();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const Public = ({ children }) => <PublicLayout>{children}</PublicLayout>;
const Admin = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <SettingsProvider>
            <AuthProvider>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Public><Home /></Public>} />
                <Route path="/opportunites" element={<Public><OpportunitiesList /></Public>} />
                <Route path="/opportunites/:slug" element={<Public><OpportunityDetail /></Public>} />
                <Route path="/projets" element={<Public><ProjectsList /></Public>} />
                <Route path="/projets/:slug" element={<Public><ProjectDetail /></Public>} />
                <Route path="/investissement" element={<Public><InvestmentsList /></Public>} />
                <Route path="/investissement/:slug" element={<Public><InvestmentDetail /></Public>} />
                <Route path="/partenaires" element={<Public><PartnersList /></Public>} />
                <Route path="/partenaires/:slug" element={<Public><PartnerDetail /></Public>} />
                <Route path="/experts" element={<Public><ExpertsList /></Public>} />
                <Route path="/experts/:slug" element={<Public><ExpertDetail /></Public>} />
                <Route path="/sourcing" element={<Public><SourcingList /></Public>} />
                <Route path="/sourcing/:slug" element={<Public><SourcingDetail /></Public>} />
                <Route path="/marches" element={<Public><MarketsList /></Public>} />
                <Route path="/marches/:country" element={<Public><MarketDetail /></Public>} />
                <Route path="/marches/:country/:sector" element={<Public><MarketDetail /></Public>} />
                <Route path="/market-test" element={<Public><MarketTestPage /></Public>} />
                <Route path="/category/:category" element={<Public><CategoryPage /></Public>} />
                <Route path="/listing/:id" element={<Public><ListingDetail /></Public>} />
                <Route path="/search" element={<Public><SearchResults /></Public>} />
                <Route path="/connect" element={<Public><ConnectPage /></Public>} />
                <Route path="/insights" element={<Public><InsightsPage /></Public>} />
                <Route path="/insights/:slug" element={<Public><InsightDetail /></Public>} />
                <Route path="/about" element={<Public><AboutPage /></Public>} />

                <Route path="/signup" element={<Public><SignupPage /></Public>} />
                <Route path="/login" element={<Public><MemberLogin /></Public>} />
                <Route path="/verify-email" element={<Public><VerifyEmail /></Public>} />
                <Route path="/account" element={<MemberRoute><Public><Account /></Public></MemberRoute>} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Admin><Dashboard /></Admin>} />
                <Route path="/admin/listings" element={<Admin><ListingsAdmin /></Admin>} />
                <Route path="/admin/listings/new" element={<Admin><ListingEditor /></Admin>} />
                <Route path="/admin/listings/:id/edit" element={<Admin><ListingEditor /></Admin>} />
                <Route path="/admin/submissions" element={<Admin><SubmissionsAdmin /></Admin>} />
                <Route path="/admin/matching" element={<Admin><MatchingAdmin /></Admin>} />
                <Route path="/admin/opportunities" element={<Admin><OpportunitiesAdmin /></Admin>} />
                <Route path="/admin/projects" element={<Admin><ProjectsAdmin /></Admin>} />
                <Route path="/admin/investments" element={<Admin><InvestmentsAdmin /></Admin>} />
                <Route path="/admin/partners" element={<Admin><PartnersAdmin /></Admin>} />
                <Route path="/admin/expert-profiles" element={<Admin><ExpertProfilesAdmin /></Admin>} />
                <Route path="/admin/sourcing" element={<Admin><SourcingAdmin /></Admin>} />
                <Route path="/admin/markets" element={<Admin><MarketsAdmin /></Admin>} />
                <Route path="/admin/market-tests" element={<Admin><MarketTestsAdmin /></Admin>} />
                <Route path="/admin/requests" element={<Admin><RequestsAdmin /></Admin>} />
                <Route path="/admin/suggestions" element={<Admin><SuggestionsAdmin /></Admin>} />
                <Route path="/admin/experts" element={<Admin><ExpertsAdmin /></Admin>} />
                <Route path="/admin/insights" element={<Admin><InsightsAdmin /></Admin>} />
                <Route path="/admin/media" element={<Admin><MediaManager /></Admin>} />
                <Route path="/admin/taxonomies" element={<Admin><TaxonomiesAdmin /></Admin>} />
                <Route path="/admin/settings" element={<Admin><SettingsAdmin /></Admin>} />
                <Route path="/admin/users" element={<Admin><UsersAdmin /></Admin>} />
                <Route path="/admin/access" element={<Admin><AccessAdmin /></Admin>} />
              </Routes>
              <Toaster position="top-right" />
            </AuthProvider>
          </SettingsProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
